import {getDayHours, isDayClosed, isLeave, REST_FULL_DAY, restDaysForDay} from "../../calander/restDays";

// Status of one employee on one work day, from the punches and the calendar
// (the Repos / Congé rows and opening hours the tablets also enforce).
//
// A punch always wins: someone who punched was there. Without a punch, the day
// is explained by the calendar first. It only counts as an absence when it's
// over (before today), and not before the first punch recorded in the period
// (the terminal wasn't in service yet).

const dateOf = (key) => new Date(`${key}T12:00:00`);

const pad = (n) => String(n).padStart(2, "0");
const keyOf = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const eachDate = (from, to) => {
    const dates = [];
    for (const cursor = dateOf(from); keyOf(cursor) <= to; cursor.setDate(cursor.getDate() + 1)) {
        dates.push(keyOf(cursor));
    }
    return dates;
};

export const firstPunchDate = (days) => days.reduce((min, d) => (!min || d.date < min ? d.date : min), null);

// Do these rests leave no working time that day? Compared with the opening hours
// when configured (repos 9h-20h in a shop open 9h-20h), otherwise with the tablets'
// "journée entière". Several slots add up (9h-14h + 14h-20h).
const coversWorkingHours = (rests, hours, date) => {
    const day = getDayHours(hours, date);
    const open = day && !day.closed ? Number(day.start) : REST_FULL_DAY.start_time;
    const close = day && !day.closed ? Number(day.end) : REST_FULL_DAY.end_time;
    let reached = open;
    [...rests]
        .sort((a, b) => Number(a.start_time) - Number(b.start_time))
        .forEach((rest) => {
            if (Number(rest.start_time) <= reached) reached = Math.max(reached, Number(rest.end_time));
        });
    return reached >= close;
};

// `expected: false` (deactivated employee, unassigned badge) and `calendarReady: false`
// (repos not loaded: nothing proves the day wasn't one) never produce an absence.
export const resolveDay = ({date, day, employeeId, restDays, hours, today, since, expected = true, calendarReady = true}) => {
    const asDate = dateOf(date);
    const rests = employeeId ? restDaysForDay(restDays, asDate, employeeId) : [];
    const off = rests.length > 0 && coversWorkingHours(rests, hours, asDate);
    const base = {date, day: day || null, rests};

    if (day) {
        const status = !day.open ? "present" : date < today ? "missing_out" : "open";
        return {...base, status, workedOnRest: off};
    }
    if (isDayClosed(hours, asDate)) return {...base, status: "closed"};
    if (off) return {...base, status: rests.some(isLeave) ? "leave" : "rest"};
    if (!expected || !calendarReady || date > today) return {...base, status: "none"};
    if (date === today) return {...base, status: "pending"};
    if (!since || date < since) return {...base, status: "none"};
    return {...base, status: "absent"};
};

// One person over the period: a resolved day per date, plus the counters.
export const buildPersonRow = ({person, days, dates, restDays, hours, today, since, calendarReady}) => {
    const byDate = Object.fromEntries(days.map((d) => [d.date, d]));
    const cells = dates.map((date) => resolveDay({
        date,
        day: byDate[date],
        employeeId: person.unknown ? null : person.key,
        restDays,
        hours,
        today,
        since,
        expected: !person.unknown && person.active !== false,
        calendarReady,
    }));
    const count = (...statuses) => cells.filter((c) => statuses.includes(c.status)).length;
    return {
        ...person,
        cells,
        worked: count("present", "open", "missing_out"),
        minutes: days.reduce((sum, d) => sum + d.worked_minutes, 0),
        missingOut: count("missing_out"),
        open: count("open"),
        absences: count("absent"),
        pending: count("pending"),
        rests: count("rest"),
        leaves: count("leave"),
    };
};

// Every person to follow: active employees with a badge (they may never have
// punched: those are the most absent), and whoever has punches in the period.
export const buildRows = ({summary, from, to, restDays, hours, today, calendarReady}) => {
    const dates = eachDate(from, to);
    const since = firstPunchDate(summary.days);
    const employeesById = Object.fromEntries(summary.employees.map((e) => [e.id, e]));
    const persons = {};

    summary.employees
        .filter((e) => e.active && e.badge_number != null)
        .forEach((e) => {
            persons[e.id] = {key: e.id, name: e.name, badge: e.badge_number, active: true, unknown: false, days: []};
        });
    summary.days.forEach((day) => {
        const employee = employeesById[day.employee_id];
        const person = persons[day.employee_key] || (persons[day.employee_key] = {
            key: day.employee_key,
            name: day.employee_id
                ? employee?.name || "Employé supprimé"
                : `Badge n° ${day.badge_number} (non attribué)`,
            badge: day.badge_number,
            active: !!employee?.active,
            unknown: !day.employee_id,
            days: [],
        });
        person.days.push(day);
    });

    return Object.values(persons)
        .map(({days, ...person}) => buildPersonRow({person, days, dates, restDays, hours, today, since, calendarReady}))
        .sort((a, b) => a.name.localeCompare(b.name, "fr"));
};

// ─── Day timeline ────────────────────────────────────────────────────────────
// Minutes counted from midnight of the work day: with a day start at 05:00, a
// 01:30 punch belongs to the night of the previous day, so it comes after 23:00.

const toMinutes = (time) => {
    const [h, m] = String(time).split(":").map(Number);
    return h * 60 + m;
};

export const workDayMinutes = (time, dayStart = "05:00") => {
    const minutes = toMinutes(time);
    return minutes < toMinutes(dayStart) ? minutes + 24 * 60 : minutes;
};

// [{start, end, open}] of the kept punches: entrée → sortie pairs. A missing
// sortie stops at `now` while the day is running, otherwise there's nothing to draw.
export const workSegments = (day, dayStart, now = null) => {
    const kept = day.punches.filter((p) => !p.duplicate).map((p) => workDayMinutes(p.time, dayStart));
    const segments = [];
    for (let i = 0; i < kept.length; i += 2) {
        if (i + 1 < kept.length) segments.push({start: kept[i], end: kept[i + 1], open: false});
        else if (now != null && now > kept[i]) segments.push({start: kept[i], end: now, open: true});
    }
    return segments;
};

// Hours shown by the timeline: the opening hours (8h-20h when none are
// configured), widened to whole hours around any punch outside them.
export const timelineWindow = (rows, date, hours, dayStart) => {
    const opening = getDayHours(hours, dateOf(date));
    let start = opening && !opening.closed ? Number(opening.start) * 60 : 8 * 60;
    let end = opening && !opening.closed ? Number(opening.end) * 60 : 20 * 60;
    rows.forEach((row) => {
        (row.cells[0]?.day?.punches || []).forEach((punch) => {
            const minutes = workDayMinutes(punch.time, dayStart);
            start = Math.min(start, Math.floor(minutes / 60) * 60);
            end = Math.max(end, Math.ceil(minutes / 60) * 60);
        });
    });
    return {start, end};
};
